// src/screens/MixerScreen.js
//
// Home screen: layer rain/birds/water/melody together, then set a sleep
// timer that fades everything out gently over the last few minutes instead
// of stopping abruptly.
//
// Custom tracks imported by the user (via the Library screen or the inline
// "+ Import" button here) appear below the built-in tiles so you can layer
// personal audio right alongside the ambient sounds.

import React, { useEffect, useState } from 'react';
import { View, Text, Image, ScrollView, Pressable, StyleSheet, ActivityIndicator, ImageBackground, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import SoundTile from '../components/SoundTile';
import JukeboxPicker from '../components/JukeboxPicker';
import { AMBIENT_SOUNDS } from '../data/sounds';
import { AudioEngine } from '../audio/AudioEngine';
import { useLibrary } from '../context/LibraryContext';
import { useJukebox } from '../context/JukeboxContext';
import { Colors, Radius, Typography, Fonts, useFontScale } from '../theme';

const TIMER_PRESETS = [15, 30, 45, 60, 90, 120]; // minutes

// Sounds that have jukeboxEnabled: true on the Sleep tab
const JUKEBOX_SOUNDS = AMBIENT_SOUNDS.filter((s) => s.jukeboxEnabled);

export default function MixerScreen() {
  const fontScale = useFontScale();
  const [selectedMinutes, setSelectedMinutes] = useState(60);

  const [timerActive, setTimerActive] = useState(false);
  const [remainingSec, setRemainingSec] = useState(null);
  const [importing, setImporting] = useState(false);
  const [reimportingId, setReimportingId] = useState(null);
  const [showJukebox, setShowJukebox] = useState(false);

  const { tracks, importTrack, reimportTrack, removeTrack, markTrackMissing } = useLibrary();
  const { getActiveUri } = useJukebox();

  const handleRemove = (track) => {
    Alert.alert(
      'Remove track',
      `Remove "${track.name}" from your library?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            AudioEngine.stopLayer(`custom-${track.id}`, { fadeMs: 0 }).catch(() => {});
            removeTrack(track.id);
          },
        },
      ]
    );
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await importTrack();
    } finally {
      setImporting(false);
    }
  };

  const startTimer = () => {
    AudioEngine.startSleepTimer(
      selectedMinutes,
      Math.min(10, Math.round(selectedMinutes * 0.15)),
      (secLeft) => setRemainingSec(secLeft),
      () => {
        setTimerActive(false);
        setRemainingSec(null);
      }
    );
    setTimerActive(true);
  };

  const cancelTimer = () => {
    AudioEngine.cancelSleepTimer();
    setTimerActive(false);
    setRemainingSec(null);
  };

  const formatTime = (sec) => {
    if (sec == null) return '';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  };

  return (
    <ImageBackground
      source={require('../../assets/backgrounds/bg_sleep.jpg')}
      style={styles.screen}
      imageStyle={styles.bgImage}
    >
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content}>
      {/* ── Tonight's Mix heading ────────────────────────────────────── */}
      <Text style={styles.tonightHeading}>Tonight's Mix</Text>

      {/* ── Jukebox entry point — directly under heading ─────────────── */}
      <Pressable
        style={styles.jukeboxBtn}
        onPress={() => setShowJukebox(true)}
        accessibilityRole="button"
        accessibilityLabel="Open Jukebox to swap sound versions"
      >
        <View style={styles.jukeboxIconCircle}>
          <Ionicons name="radio" size={18} color={Colors.accent} />
        </View>
        <View style={styles.jukeboxTextGroup}>
          <View style={styles.jukeboxTitleRow}>
            <Text style={styles.jukeboxTitle}>Jukebox</Text>
            <View style={styles.jukeboxBadge}>
              <Text style={styles.jukeboxBadgeText}>FREE</Text>
            </View>
          </View>
          <Text style={styles.jukeboxSub}>Swap sound versions · {JUKEBOX_SOUNDS.length} sounds</Text>
          <Text style={styles.jukeboxHint}>Turn a sound on first, then swap its version here.</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color={Colors.textMuted} />
      </Pressable>

      {/* ── Logo ─────────────────────────────────────────────────────── */}
      <Image
        source={require('../../assets/sleepmix_logo.png')}
        style={styles.brandLogo}
        resizeMode="contain"
        accessibilityLabel="SleepMixBlueprint"
      />

      <Text style={styles.subheader}>
        Layer sounds and adjust each one. They'll fade in as you turn them on.
      </Text>

      {/* ── Sound tiles — use jukebox-selected URI where applicable ─── */}
      {AMBIENT_SOUNDS.map((s) => (
        <SoundTile
          key={s.id}
          id={s.id}
          name={s.name}
          uri={getActiveUri('sleep', s)}
          icon={s.icon}
        />
      ))}

      {/* ── Custom / imported tracks ─────────────────────────────────── */}
      <View style={styles.sectionRow}>
        <View style={styles.sectionLabelGroup}>
          <Ionicons name="headset" size={13} color={Colors.textMuted} style={styles.sectionIcon} />
          <Text style={styles.sectionLabel}>Your tracks</Text>
        </View>
        <Pressable style={styles.importButton} onPress={handleImport} disabled={importing}>
          {importing ? (
            <ActivityIndicator size="small" color={Colors.textOnAccent} />
          ) : (
            <>
              <Ionicons name="add" size={14} color={Colors.textOnAccent} />
              <Text style={styles.importText}>Import</Text>
            </>
          )}
        </Pressable>
      </View>

      {tracks.length === 0 ? (
        <View style={styles.emptyCustom}>
          <Ionicons name="musical-notes-outline" size={28} color={Colors.textMuted} />
          <Text style={styles.emptyCustomText}>
            Tap Import to add your own audio files — they'll mix right alongside the built-in sounds.
          </Text>
        </View>
      ) : (
        tracks.map((t) => (
          <SoundTile
            key={t.id}
            id={`custom-${t.id}`}
            name={t.name}
            uri={t.uri}
            artworkUri={t.artworkUri}
            icon="musical-notes"
            onRemove={() => handleRemove(t)}
            needsReimport={!!t.needsReimport}
            importing={reimportingId === t.id}
            onReimport={t.needsReimport ? async () => {
              setReimportingId(t.id);
              try { return await reimportTrack(t.id); } finally { setReimportingId(null); }
            } : undefined}
            onMissing={!t.needsReimport ? () => markTrackMissing(t.id) : undefined}
          />
        ))
      )}

      <View style={styles.timerCard}>
        <Text allowFontScaling={false} style={[styles.timerTitle, { fontSize: 16 * fontScale }]}>Sleep timer</Text>
        <Text style={styles.timerSub}>
          Sound fades out gently as the timer ends — no jarring silence.
        </Text>

        <View style={styles.presetRow}>
          {TIMER_PRESETS.map((m) => (
            <Pressable
              key={m}
              onPress={() => setSelectedMinutes(m)}
              style={[styles.presetPill, selectedMinutes === m && styles.presetPillActive]}
            >
              <Text
                allowFontScaling={false}
                style={[
                  styles.presetText,
                  selectedMinutes === m && styles.presetTextActive,
                  { fontSize: 13 * fontScale },
                ]}
              >
                {m}m
              </Text>
            </Pressable>
          ))}
        </View>

        {timerActive ? (
          <Pressable style={[styles.actionButton, styles.cancelButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]} onPress={cancelTimer}>
            <Text
              allowFontScaling={false}
              style={[styles.actionText, styles.cancelText, { fontSize: 15 * fontScale }]}
            >
              Cancel · {formatTime(remainingSec)}
            </Text>
          </Pressable>
        ) : (
          <Pressable style={[styles.actionButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]} onPress={startTimer}>
            <Text
              allowFontScaling={false}
              style={[styles.actionText, { fontSize: 15 * fontScale }]}
            >
              Start {selectedMinutes}-minute timer
            </Text>
          </Pressable>
        )}
      </View>
    </ScrollView>

    {/* ── Jukebox picker modal ─────────────────────────────────────── */}
    <JukeboxPicker
      visible={showJukebox}
      onClose={() => setShowJukebox(false)}
      sounds={JUKEBOX_SOUNDS}
      tabId="sleep"
    />
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  screen:    { flex: 1, backgroundColor: Colors.bg },
  bgImage:   { opacity: 0.18 },
  content:   { padding: 16, paddingBottom: 32 },
  tonightHeading: {
    color: Colors.textBright,
    fontFamily: Fonts.bold,
    fontSize: 22,
    marginBottom: 12,
    marginTop: 4,
  },
  brandLogo: {
    width: 260,
    height: 80,
    alignSelf: 'center',
    marginBottom: 16,
  },

  // Jukebox button
  jukeboxBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Colors.surface,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.accent + '33',
    padding: 14,
    marginBottom: 18,
  },
  jukeboxIconCircle: {
    width: 38,
    height: 38,
    borderRadius: Radius.full,
    backgroundColor: Colors.accent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Colors.accent + '30',
  },
  jukeboxTextGroup: { flex: 1 },
  jukeboxTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 2 },
  jukeboxTitle: {
    fontFamily: Fonts.bold,
    fontSize: 15,
    color: Colors.textBright,
  },
  jukeboxBadge: {
    backgroundColor: Colors.accent + '22',
    borderRadius: Radius.pill,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: Colors.accent + '44',
  },
  jukeboxBadgeText: {
    fontFamily: Fonts.bold,
    fontSize: 9,
    color: Colors.accent,
    letterSpacing: 0.8,
  },
  jukeboxSub: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textSecondary,
  },
  jukeboxHint: {
    fontFamily: Fonts.regular,
    fontSize: 11,
    color: Colors.textMuted,
    marginTop: 2,
  },

  subheader: { color: Colors.textSecondary, ...Typography.caption, marginBottom: 18 },

  timerCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    padding: 16,
    marginTop: 12,
  },
  timerTitle: { color: Colors.textBright, ...Typography.title },
  timerSub:   { color: Colors.textSecondary, ...Typography.small, marginTop: 4, marginBottom: 14 },

  presetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  presetPill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: Radius.pill,
    backgroundColor: Colors.surface2,
  },
  presetPillActive: { backgroundColor: Colors.accent },
  presetText:       { color: Colors.textSecondary, ...Typography.label },
  presetTextActive: { color: Colors.textOnAccent, fontFamily: Fonts.bold },

  actionButton: {
    backgroundColor: Colors.accent,
    borderRadius: Radius.md,
    paddingVertical: 14,
    alignItems: 'center',
  },
  cancelButton: { backgroundColor: Colors.danger },
  actionText:   { color: Colors.textOnAccent, ...Typography.button },
  cancelText:   { color: Colors.dangerText },

  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 22,
    marginBottom: 10,
  },
  sectionLabelGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  sectionIcon: { marginTop: 1 },
  sectionLabel: {
    color: Colors.textSecondary,
    ...Typography.label,
    fontFamily: Fonts.semiBold,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Colors.accent,
    borderRadius: Radius.pill,
    paddingVertical: 6,
    paddingHorizontal: 12,
    minWidth: 72,
    justifyContent: 'center',
  },
  importText: {
    color: Colors.textOnAccent,
    ...Typography.small,
    fontFamily: Fonts.bold,
  },
  emptyCustom: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    gap: 10,
  },
  emptyCustomText: {
    color: Colors.textMuted,
    ...Typography.small,
    textAlign: 'center',
    lineHeight: 17,
  },
});
